import React, { useCallback, useState } from 'react';
import { Modal } from 'reactstrap';

/**
 * Componente de Modal não controlado. O estado de abertura/fechamento
 * é gerenciado internamente pelo próprio componente. O conteúdo do
 * modal (header, body, footer) pode ser fornecido como strings, componentes React ou funções.
 *
 * @param {Object} props
 * @param {string|React.ReactNode} [props.header] - Título do modal como string ou um componente React para o cabeçalho.
 * @param {string|React.ReactNode} [props.body] - Conteúdo do corpo do modal como string ou um componente React.
 * @param {string|React.ReactNode|Function} [props.footer] - Conteúdo do rodapé do modal. Pode ser string, componente React, ou uma função que recebe `{ closeModal }` como argumento.
 * @param {Function} props.onModalToggle - Função de callback chamada quando o modal é aberto ou fechado.
 * @param {string} [props.title=''] - Título do modal (usado se `header` for uma string).
 * @param {string} [props.subtitle=''] - Subtítulo do modal (usado se `header` for uma string).
 * @param {boolean} [props.centered=true] - Centraliza o modal na tela.
 * @param {boolean} [props.backdrop=true] - Exibe ou oculta o backdrop do modal.
 * @param {Function} [props.trigger=null] - Componente que irá acionar a abertura do modal (ex: um botão).
 * @param {any} [props.additionalProps] - Quaisquer outras propriedades passadas para o Modal.
 * @returns {JSX.Element}
 */
const UncontrolledDefaultModal = ({
  header = null,
  body = null,
  footer = null,
  onModalToggle,
  title = '',
  subtitle = '',
  centered = true,
  backdrop = true,
  trigger = null,
  ...props
}) => {
  const [open, setOpen] = useState(false);

  const toggleModal = useCallback(() => {
    const newOpenState = !open;
    setOpen(newOpenState);
    if (onModalToggle) {
      onModalToggle(newOpenState);
    }
  }, [open, onModalToggle]);

  const renderHeader = useCallback(() => {
    if (typeof header === 'function') {
      return header({ closeModal: toggleModal });
    }

    if (React.isValidElement(header)) {
      return header;
    }
    return (
      <div className="modal-header">
        <h6 className="modal-title" id="modal-title-default">
          {title}
          <br />
          {subtitle && <p style={{ marginBottom: 0 }}>{subtitle}</p>}
        </h6>
        <button
          aria-label="Close"
          className="close"
          data-dismiss="modal"
          type="button"
          onClick={toggleModal}
        >
          <span aria-hidden={true}>×</span>
        </button>
      </div>
    );
  }, [header, title, subtitle, toggleModal]);

  const renderBody = useCallback(() => {
    if (React.isValidElement(body)) {
      return body;
    }
    return <div className="modal-body">{body}</div>;
  }, [body]);

  const renderFooter = useCallback(() => {
    if (typeof footer === 'function') {
      return footer({ closeModal: toggleModal });
    }
    if (React.isValidElement(footer)) {
      return React.cloneElement(footer, { onClose: toggleModal });
    }
    return <div className="modal-footer">{footer}</div>;
  }, [footer, toggleModal]);

  return (
    <>
      {trigger &&
        React.cloneElement(trigger, {
          onClick: () => {
            // eslint-disable-next-line no-unused-expressions
            trigger?.props?.onClick?.();
            toggleModal();
          }
        })}

      <Modal
        backdrop={backdrop}
        isOpen={open}
        toggle={toggleModal}
        centered={centered}
        {...props}
      >
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </Modal>
    </>
  );
};

export default UncontrolledDefaultModal;
